import { LocalStorageService } from '@aiz-gl/omni-ui';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { computed, inject, Injectable, resource, signal } from '@angular/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import {
  BehaviorSubject,
  catchError,
  filter,
  firstValueFrom,
  lastValueFrom,
  of,
  tap,
  throwError,
} from 'rxjs';
import { AuthProfile, AuthResponse } from '../../models/auth.model';
import { RegionService } from '../region/region.service';
import { TokenProviderService } from '../token-provider/token-provider.service';
import { UserPermissionsService } from '../user-permissions/user-permissions.service';
import { mapAccessTokenToModel } from './auth.mapper';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private oktaAuth = inject(OKTA_AUTH);
  private permissions = inject(UserPermissionsService);
  private regions = inject(RegionService);
  private localStore = inject(LocalStorageService);
  private tokenProvider = inject(TokenProviderService);

  private initialized$ = new BehaviorSubject<boolean>(false);
  // Initialized returns true, once, after token and policies are loaded.
  public initialized(): Promise<boolean> {
    return firstValueFrom(
      this.initialized$.pipe(filter((initialized) => initialized)),
    );
  }

  readonly accessToken = signal<string | undefined>(undefined);
  readonly hasIdToken = computed(() => !!this.tokenProvider.idToken());
  readonly hasAccessToken = computed(() => !!this.accessToken());

  readonly isAuthenticated = computed(
    () => this.hasIdToken() && this.hasAccessToken(),
  );

  readonly userName = computed(
    () => this.tokenProvider.rawToken()?.claims.name || '',
  );

  readonly userEmail = computed(
    () => this.tokenProvider.rawToken()?.claims.email || '',
  );

  readonly authProfile = computed((): AuthProfile | undefined => {
    const token = this.accessToken();
    if (!token) return;
    return mapAccessTokenToModel(token);
  });

  readonly userRolesString = computed(
    () => this.authProfile()?.roles.join(',') || '',
  );
  readonly userBusinessEntitiesString = computed(
    () => this.authProfile()?.businessEntities.join(',') || '',
  );

  async signIn(): Promise<void> {
    await this.oktaAuth.signInWithRedirect();
  }

  async signOut(): Promise<void> {
    await this.oktaAuth.signOut().then(() => {
      this.localStore.clear();
    });
  }

  // Uses the Okta ID Token to generate the access token.
  // All other API calls will use the access token returned by this call.
  generateToken$ = (idToken: string) => {
    const headers = new HttpHeaders().set('X-Token', idToken || '');

    return this.http
      .post<AuthResponse>('auth/generate-token', {}, { headers })
      .pipe(
        catchError((response: HttpErrorResponse) => {
          const { status, error } = response;
          if (status === 401) {
            this.signIn();
          } else {
            return throwError(() => error);
          }
          return of();
        }),
        tap(async (response: AuthResponse) => {
          const { access_token } = response;
          this.accessToken.set(access_token);
          await Promise.all([
            // Initialize permissions and regions before application start.
            this.permissions.initialize(),
            this.regions.initialize(),
          ]).then(() => {
            // The Auth Service is considered initialized.
            // The atbAccessToken resource can now take over token refreshes.
            this.initialized$.next(true);
          });
        }),
      );
  };

  // Resource used to regenerate the access token when the Okta ID token refreshes.
  private readonly atbAccessToken = resource({
    params: () => {
      const token = this.tokenProvider.idToken();
      const initialized = this.initialized$.value;
      return token && initialized ? token : undefined;
    },
    loader: ({ params }) => lastValueFrom(this.generateToken$(params || '')),
  });

  async handleRedirect() {
    try {
      await this.oktaAuth.handleLoginRedirect();
    } catch (error) {
      console.error('Login redirect error:', error);
    }
  }

  initialize() {
    const token = this.tokenProvider.idToken();
    return lastValueFrom(this.generateToken$(token || ''));
  }
}
